import { useState, useRef } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Tooltip,
  CircularProgress,
} from "@material-ui/core";
import { LoadingButton } from "@material-ui/lab";
import micFill from "@iconify/icons-eva/mic-fill";
import stopCircleFill from "@iconify/icons-eva/stop-circle-fill";
import cloudUploadFill from "@iconify/icons-eva/cloud-upload-fill";
import flashFill from "@iconify/icons-eva/flash-fill";
import edit2Fill from "@iconify/icons-eva/edit-2-fill";
import arrowLeftFill from "@iconify/icons-eva/arrow-left-fill";
import Iconify from "src/components/iconify";
import PropTypes from "prop-types";
import { propuestasApi } from "src/services/propuestasApi";
import { useSnackbar } from "src/components/snackbar";
import { useConfirmDiscard } from "./shared/useConfirmDiscard";
import DialogDescartarCambios from "./shared/DialogDescartarCambios";
import { BRAND } from "src/theme/brandTokens";

const MAX_AUDIO_BYTES = 16 * 1024 * 1024; // 16 MB

/**
 * Gate previo standalone que aparece antes del Stepper de propuesta V2 cuando
 * la propuesta está vacía. El vendor describe la idea (texto, voz o audio
 * subido). La IA genera el borrador completo, o el vendor puede saltar el gate
 * con "Completar manualmente". El texto tipeado se preserva como
 * `promptOriginal` independientemente de la opción elegida — queda en BD como
 * rastro útil para regeneración futura via el chip "Generado con IA · ver
 * prompt" del Stepper.
 *
 * Contrato:
 *   onCompletar({ borrador, promptOriginal }) — borrador es el objeto retornado
 *     por el endpoint generarConIA, o null si el vendor eligió "Completar
 *     manualmente". promptOriginal es el texto del textfield (string,
 *     posiblemente vacío).
 *   onCancelar() — sale del flujo de creación (típicamente al listado de
 *     propuestas).
 */
const GateIdea = ({ onCompletar, onCancelar, sucursal }) => {
  const { enqueueSnackbar } = useSnackbar();

  const [idea, setIdea] = useState("");
  const [grabando, setGrabando] = useState(false);

  const { requestLeave, dialogOpen, onSeguirEditando, onDescartar } =
    useConfirmDiscard({ dirty: idea.trim().length > 0 });

  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const inputAudioRef = useRef(null);

  const [generarConIA, { isLoading: isGenerando }] =
    propuestasApi.useGenerarConIAMutation();
  const [transcribirAudio, { isLoading: isTranscribiendo }] =
    propuestasApi.useTranscribirAudioMutation();

  const bloqueado = isGenerando || isTranscribiendo;

  // ── Grabación de voz ───────────────────────────────────────────────────────

  const handleGrabar = async () => {
    if (grabando) {
      mediaRecorderRef.current?.stop();
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      chunksRef.current = [];

      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      mediaRecorder.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        setGrabando(false);

        const mimeType = mediaRecorder.mimeType || "audio/webm";
        const blob = new Blob(chunksRef.current, { type: mimeType });
        const extension = mimeType.split("/")[1]?.split(";")[0] || "webm";
        const file = new File([blob], `grabacion.${extension}`, {
          type: mimeType,
        });

        await enviarAudio(file);
      };

      mediaRecorder.start();
      setGrabando(true);
    } catch {
      enqueueSnackbar(
        "No se pudo acceder al micrófono. Verifica los permisos del navegador.",
        { variant: "error" },
      );
    }
  };

  // ── Subida de archivo de audio ─────────────────────────────────────────────

  const handleSeleccionarArchivo = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";

    if (file.size > MAX_AUDIO_BYTES) {
      enqueueSnackbar(
        "El archivo supera el tamaño máximo permitido de 16 MB.",
        { variant: "error" },
      );
      return;
    }

    enviarAudio(file);
  };

  // ── Transcripción ──────────────────────────────────────────────────────────

  const enviarAudio = async (file) => {
    const formData = new FormData();
    formData.append("audio", file);

    try {
      const resultado = await transcribirAudio(formData).unwrap();
      setIdea((prev) =>
        prev.trim() ? `${prev.trim()}\n\n${resultado.texto}` : resultado.texto,
      );
    } catch {
      enqueueSnackbar("No se pudo transcribir el audio. Intenta nuevamente.", {
        variant: "error",
      });
    }
  };

  // ── Acciones principales ───────────────────────────────────────────────────

  const handleGenerar = async () => {
    if (!idea.trim()) return;

    try {
      const resultado = await generarConIA({
        idea: idea.trim(),
        ...(sucursal ? { sucursal } : {}),
      }).unwrap();

      onCompletar({
        borrador: resultado.borrador,
        promptOriginal: idea.trim(),
      });
    } catch {
      enqueueSnackbar("No se pudo generar la propuesta. Intenta nuevamente.", {
        variant: "error",
      });
    }
  };

  const handleCompletarManual = () => {
    onCompletar({ borrador: null, promptOriginal: idea.trim() });
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <Box>
      {/* Botón "Volver al listado" arriba a la izquierda */}
      <Box sx={{ mb: 4 }}>
        <Tooltip title="Volver al listado de propuestas">
          <Button
            variant="text"
            color="inherit"
            onClick={() => requestLeave(onCancelar)}
            disabled={bloqueado || grabando}
            startIcon={<Iconify icon={arrowLeftFill} width={18} height={18} />}
            sx={{ color: "text.secondary" }}
          >
            Volver al listado
          </Button>
        </Tooltip>
      </Box>

      {/* Encabezado */}
      <Box sx={{ textAlign: "center", maxWidth: 720, mx: "auto", mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          ¿Cuál es la idea de la propuesta?
        </Typography>
        <Typography variant="body2" color="textSecondary">
          Describe el servicio. La IA puede generar el borrador completo, o
          puedes completar manualmente.
        </Typography>
      </Box>

      {/* Textfield */}
      <Box sx={{ maxWidth: 720, mx: "auto", mb: 2 }}>
        <TextField
          multiline
          minRows={8}
          fullWidth
          variant="outlined"
          placeholder="Ej: El cliente necesita asesoría tributaria mensual para su empresa de transporte. Tiene problemas con el F29 y necesita apoyo en la declaración de renta anual..."
          value={idea}
          onChange={(e) => setIdea(e.target.value)}
          disabled={isGenerando}
        />
      </Box>

      {/* Botones de entrada de audio */}
      <Box
        sx={{
          maxWidth: 720,
          mx: "auto",
          display: "flex",
          justifyContent: "center",
          gap: 1,
          mb: 4,
          flexWrap: "wrap",
        }}
      >
        <Tooltip title={grabando ? "Detener grabación" : "Grabar voz"}>
          <span>
            <Button
              variant="outlined"
              color={grabando ? "secondary" : "inherit"}
              disabled={bloqueado}
              onClick={handleGrabar}
              startIcon={
                <Iconify
                  icon={grabando ? stopCircleFill : micFill}
                  width={18}
                  height={18}
                />
              }
            >
              {grabando ? "Detener" : "Grabar"}
            </Button>
          </span>
        </Tooltip>

        <Tooltip title="Subir archivo de audio (.mp3, .wav, .m4a, .ogg, .webm — máx. 16 MB)">
          <span>
            <Button
              variant="outlined"
              color="inherit"
              disabled={bloqueado || grabando}
              onClick={() => inputAudioRef.current?.click()}
              startIcon={
                <Iconify icon={cloudUploadFill} width={18} height={18} />
              }
            >
              Subir audio
            </Button>
          </span>
        </Tooltip>

        <input
          ref={inputAudioRef}
          type="file"
          accept=".mp3,.wav,.m4a,.ogg,.webm"
          style={{ display: "none" }}
          onChange={handleSeleccionarArchivo}
        />

        {isTranscribiendo && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <CircularProgress size={16} />
            <Typography variant="caption" color="textSecondary">
              Transcribiendo...
            </Typography>
          </Box>
        )}
      </Box>

      {/* Acciones principales */}
      <Box
        sx={{
          maxWidth: 720,
          mx: "auto",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <Tooltip
          title={
            !idea.trim()
              ? "Escribe o graba una idea primero"
              : "Generar borrador completo con IA (puede tardar hasta 20 segundos)"
          }
        >
          <span>
            <LoadingButton
              variant="contained"
              loading={isGenerando}
              disabled={!idea.trim() || isTranscribiendo || grabando}
              onClick={handleGenerar}
              startIcon={
                !isGenerando && (
                  <Iconify icon={flashFill} width={18} height={18} />
                )
              }
              sx={{
                bgcolor:
                  idea.trim() && !isTranscribiendo && !grabando
                    ? BRAND.color.primary
                    : undefined,
                "&:hover": { bgcolor: BRAND.color.primaryDark },
              }}
            >
              {isGenerando
                ? "Generando propuesta..."
                : "Generar propuesta con IA"}
            </LoadingButton>
          </span>
        </Tooltip>

        <Button
          variant="text"
          color="inherit"
          disabled={bloqueado || grabando}
          onClick={handleCompletarManual}
          startIcon={<Iconify icon={edit2Fill} width={18} height={18} />}
        >
          Completar manualmente
        </Button>
      </Box>

      <DialogDescartarCambios
        open={dialogOpen}
        onSeguir={onSeguirEditando}
        onDescartar={onDescartar}
      />
    </Box>
  );
};

GateIdea.propTypes = {
  onCompletar: PropTypes.func.isRequired,
  onCancelar: PropTypes.func.isRequired,
  sucursal: PropTypes.string,
};

GateIdea.defaultProps = {
  sucursal: undefined,
};

export default GateIdea;
